import { t } from '@/i18n';
import { axios } from '@/lib/utils/axios';

import { getResponse } from '../common';
import { API_URLS } from '../query.client';

export const PRESIGNED_UPLOAD_SCOPE = {
	BANNER: 'banner',
	ASSET: 'asset',
	INSPECTION: 'inspection',
	DAMAGE: 'damage',
	// Conversion source (.glb/.ply/.dxf/.ifc) staged in the tile bucket the converter
	// reads from. Uploaded straight to S3 like any other scope, so a ~1 GB model
	// never passes through the backend.
	TILESET: 'tileset',
} as const;
export type PRESIGNED_UPLOAD_SCOPE = (typeof PRESIGNED_UPLOAD_SCOPE)[keyof typeof PRESIGNED_UPLOAD_SCOPE];

export type GetPresignedUrlRequest = {
	scope: PRESIGNED_UPLOAD_SCOPE;
	assetId: string;
	inspectionId?: string;
	damageId?: string;
	originalFilename: string;
	contentType: string;
	fileSize: number;
};

export type GetPresignedUrlResponse = {
	key: string;
	uploadUrl: string;
	expiresIn: number;
	requiredHeaders: Record<string, string>;
};

const getPresignedUrl = async (req: GetPresignedUrlRequest): Promise<GetPresignedUrlResponse | null> => {
	const response = await axios.post(API_URLS.FILE_UPLOAD.PRESIGNED_URL, req);
	return getResponse<GetPresignedUrlResponse>(response.data);
};

export type PresignedUploadOptions = {
	/** Percentage of the file transferred, 0-100. */
	onProgress?: (percent: number) => void;
	signal?: AbortSignal;
};

/**
 * Uploads a file directly to S3 using a presigned URL.
 * Note: Uses plain fetch (not axios) to avoid injecting the auth token into the S3 request.
 *
 * Switches to XHR when the caller wants progress: `fetch` cannot report upload
 * progress at all, and a conversion source takes long enough that a bar with no
 * movement reads as a hang.
 */
/** Translated message for the user; the technical reason stays on `cause` for logs and Sentry. */
const uploadError = (reason: string) => new Error(t('files.upload.presignFailed'), { cause: reason });

const uploadToS3 = async (
	uploadUrl: string,
	file: File,
	requiredHeaders: Record<string, string>,
	options: PresignedUploadOptions = {},
): Promise<void> => {
	if (!options.onProgress) {
		const response = await fetch(uploadUrl, {
			method: 'PUT',
			body: file,
			headers: requiredHeaders,
			signal: options.signal,
		});

		if (!response.ok) {
			throw uploadError(`S3 upload failed: ${response.status} ${response.statusText}`);
		}
		return;
	}

	await new Promise<void>((resolve, reject) => {
		const xhr = new XMLHttpRequest();
		xhr.open('PUT', uploadUrl);
		Object.entries(requiredHeaders).forEach(([header, value]) => xhr.setRequestHeader(header, value));
		xhr.upload.onprogress = (event) => {
			if (event.lengthComputable) {
				options.onProgress?.(Math.round((100 * event.loaded) / event.total));
			}
		};
		xhr.onload = () =>
			xhr.status >= 200 && xhr.status < 300
				? resolve()
				: reject(uploadError(`S3 upload failed: ${xhr.status} ${xhr.statusText}`));
		xhr.onerror = () => reject(uploadError('S3 upload failed: network error'));
		xhr.onabort = () => reject(new DOMException(t('files.upload.uploadCancelled'), 'AbortError'));
		options.signal?.addEventListener('abort', () => xhr.abort(), { once: true });
		xhr.send(file);
	});
};

/**
 * Full presigned-URL upload flow:
 * 1. Request a presigned URL from the backend.
 * 2. PUT the file directly to S3.
 * Returns the S3 key, which becomes the fileUrl stored in the data-upload record.
 */
export const uploadFileWithPresignedUrl = async (
	file: File,
	params: Omit<GetPresignedUrlRequest, 'originalFilename' | 'contentType' | 'fileSize'>,
	options: PresignedUploadOptions = {},
): Promise<GetPresignedUrlResponse> => {
	const presigned = await getPresignedUrl({
		...params,
		originalFilename: file.name,
		// Browsers report model formats (.glb/.ply/.dxf/.ifc) as an empty type, which
		// is not a valid MIME string; the backend keys these off the filename.
		contentType: file.type || 'application/octet-stream',
		fileSize: file.size,
	});

	if (!presigned) {
		throw uploadError('Failed to obtain presigned URL.');
	}

	await uploadToS3(presigned.uploadUrl, file, presigned.requiredHeaders, options);

	return presigned;
};
